import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Post } from '../lib/types';
import { WatchButton } from './WatchButton';
import { Text } from './ui';
import './LotCard.css';

interface LotCardProps {
  /** Must have post.auction populated - non-auction posts should not render this card. */
  post: Post;
  showWatch?: boolean;
  /** Base path for the lot link (default '/p'). The Saleroom v2 grid passes
   *  '/saleroom2/p' so cards link to the stacked lot pages. */
  hrefBase?: string;
}

/**
 * Sales Room + Watchlist grid card. One auctioned post per card. Hero
 * image, state pill, title + LOT number, price row + meta line, and the
 * watch button at the bottom. The whole card is a <Link> to the post
 * detail; the watch button stops propagation so the heart tap doesn't
 * navigate.
 */
export function LotCard({ post, showWatch = true, hrefBase = '/p' }: LotCardProps) {
  const auction = post.auction;
  if (!auction) {
    // Defensive: a LotCard with no auction is meaningless. Render a thin
    // text-only fallback so callers that defensively pipe non-auction
    // posts through here don't crash. (Watchlist already routes these
    // through a placeholder branch.)
    return (
      <Link to={`${hrefBase}/${post.slug}`} className="lot-card lot-card--fallback">
        {/* Title keeps the card's Carmen/clamp/hover-accent set-piece treatment. */}
        <h3 className="lot-card__title">{post.title}</h3>
      </Link>
    );
  }

  const hero =
    post.thumbnail_url ??
    post.media?.thumbnail_url ??
    null;

  const state = auction.state;
  const sold = state === 'ended' && auction.current_high_amount > 0;
  const stateLabel = state === 'live'
    ? 'Live'
    : state === 'scheduled'
      ? 'Opens'
      : sold ? 'Sold' : 'Unsold';

  // Price row: always the bare amount - current high once bids exist,
  // minimum bid before that. Ended lots never show a price (existing
  // product rule).
  const hasBids = auction.bid_count > 0;
  const priceCents = state === 'live' && hasBids
    ? auction.current_high_amount
    : auction.min_bid;

  // Meta line: always "bids · time" in one line. The time fragment flips
  // brick red inside the final hour of a live lot.
  const msToEnd = new Date(auction.ends_at).getTime() - Date.now();
  const urgent = state === 'live' && msToEnd > 0 && msToEnd < 3_600_000;

  let meta: ReactNode = null;
  if (state === 'live') {
    meta = (
      <Text as="p" role="caption" muted className="lot-card__meta">
        {hasBids ? <strong>{bidsLabel(auction.bid_count)}</strong> : 'No bids yet'}
        {' · '}
        <span
          className={
            urgent
              ? 'lot-card__meta-time lot-card__meta-time--urgent'
              : 'lot-card__meta-time'
          }
        >
          Ends in {countdown(auction.ends_at)}
        </span>
      </Text>
    );
  } else if (state === 'scheduled') {
    // Within a day a countdown is most useful; further out, the actual
    // weekday + local time reads better than "in 3d 4h".
    const msToStart = new Date(auction.starts_at).getTime() - Date.now();
    const starts = new Date(auction.starts_at);
    meta = (
      <Text as="p" role="caption" muted className="lot-card__meta">
        {msToStart < 86_400_000
          ? `Opens in ${countdown(auction.starts_at)}`
          : `Opens ${starts.toLocaleDateString('en-US', { weekday: 'short' })} · ${starts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
      </Text>
    );
  }

  // Ended cards swap the price block for a small-caps status line.
  const endedStatus = auction.bought_now_at
    ? 'Sold · Buy now'
    : sold
      ? hasBids ? `Sold · ${bidsLabel(auction.bid_count)}` : 'Sold'
      : 'Unsold';

  return (
    <Link
      to={`${hrefBase}/${post.slug}`}
      className={`lot-card lot-card--${state}`}
      aria-label={`${auction.lot_name} - LOT ${auction.lot_number}`}
    >
      <div className="lot-card__hero">
        {hero ? (
          <img src={hero} alt="" loading="lazy" />
        ) : (
          <div className="lot-card__hero-placeholder" aria-hidden="true">
            <span>LOT</span>
          </div>
        )}
        {/* Status cue: plain black caps text, top-left of the image (no pill) -
            LIVE on a biddable lot, OPENS on an upcoming one (team, 2026-06-22). */}
        {(state === 'live' || state === 'scheduled') && (
          <span className="lot-card__status">{stateLabel.toUpperCase()}</span>
        )}
        {/* Favorite heart: a bare icon top-right of the image (marketplace
            pattern). Non-ended lots only; toggles the same favorite mechanism. */}
        {showWatch && state !== 'ended' && (
          <WatchButton
            postId={post.id}
            initialWatching={Boolean(auction.viewer_watching)}
            initialCount={auction.watch_count}
            variant="overlay"
            className="lot-card__fav"
          />
        )}
      </div>

      <div className="lot-card__body">
        {/* Title on the left; the LOT number right-aligned to the card's right
            margin, on the title's first line. */}
        <div className="lot-card__title-row">
          <h3 className="lot-card__title">{auction.lot_name}</h3>
          <span className="lot-card__lot-num">LOT {auction.lot_number}</span>
        </div>
        {state === 'ended' ? (
          /* Montserrat small-caps status (set-piece); not a body Text role. */
          <p className="lot-card__status-ended">{endedStatus}</p>
        ) : (
          <>
            <div className="lot-card__price-row">
              {/* Price keeps its Montserrat/tnum figure treatment (set-piece). */}
              <span className="lot-card__price">{dollars(priceCents)}</span>
              {/* Small grey italic label naming the figure. */}
              <span className="lot-card__price-label">current bid</span>
              {auction.shipping_cost != null && (
                <Text as="span" role="small" muted className="lot-card__postage">
                  {auction.shipping_cost === 0
                    ? 'Free shipping'
                    : `+ ${dollars(auction.shipping_cost)} shipping`}
                </Text>
              )}
            </div>
            {meta}
          </>
        )}
      </div>
    </Link>
  );
}

function dollars(cents: number): string {
  // Always two decimals ("$40.00") - matches the lot page's formatter.
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function bidsLabel(count: number): string {
  return count === 1 ? '1 bid' : `${count} bids`;
}

/**
 * Two-unit countdown for lot timers: "1d 6h" above 24 hours, "2h 14m"
 * under, dropping a leading zero unit ("14m"). Clamps at "0m" once the
 * moment passes - the state pill corrects itself on the next fetch.
 */
function countdown(iso: string): string {
  const totalMinutes = Math.floor(
    Math.max(0, new Date(iso).getTime() - Date.now()) / 60_000,
  );
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
